import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getMetricsSeries } from '../../services/cloneProvider';
import { getErrorMessage } from './badges';

const REFRESH_INTERVAL_MS = 60 * 1000;

const COLOR_BLUE = '#3b82f6';
const COLOR_RED = '#dc3545';
const COLOR_GREEN = '#22c55e';

const SERVICE_TYPE_LABEL = { fb_ql: 'FB QL', fb_eaaaau: 'FB EAAAAU', fb_eaagno: 'FB EAAGNO' };

function formatMinuteLabel(epochMinute) {
  const date = new Date(epochMinute * 60 * 1000);
  return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

// Mỗi series trong `metrics` (shape backend's MetricKey -> [{minute, value}])
// cùng chung 1 trục thời gian (30 điểm, cùng epoch-minute) - join lại thành
// 1 array record theo index, record[i].minute lấy từ series đầu tiên.
function zipByMinute(metrics, keys) {
  const base = metrics[keys[0]] || [];
  return base.map((point, i) => {
    const record = { minute: formatMinuteLabel(point.minute) };
    for (const key of keys) {
      record[key] = metrics[key]?.[i]?.value ?? 0;
    }
    return record;
  });
}

function RequestBarChart({ title, data, dataKey }) {
  return (
    <div className="rounded-xl border border-cyan-100 bg-white p-1.5">
      <h4 className="mb-0.5 text-[11px] font-semibold leading-tight text-slate-600">{title}</h4>
      <div className="h-[90px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} isAnimationActive={false} margin={{ top: 2, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="minute" tick={{ fontSize: 9 }} minTickGap={24} />
            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} width={28} />
            <Tooltip isAnimationActive={false} />
            <Bar dataKey={dataKey} fill={COLOR_BLUE} name="Requests" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Line "Tổng" (xanh) = success + fail, line "Fail" (đỏ) = đúng fail - tổng
// luôn >= fail, nên 2 line không bị lệch thang đo (không cần dual-axis).
function SuccessFailLineChart({ title, data, totalKey, failKey }) {
  return (
    <div className="rounded-xl border border-cyan-100 bg-white p-1.5">
      <h4 className="mb-0.5 text-[11px] font-semibold leading-tight text-slate-600">{title}</h4>
      <div className="h-[90px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} isAnimationActive={false} margin={{ top: 2, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="minute" tick={{ fontSize: 9 }} minTickGap={24} />
            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} width={28} />
            <Tooltip isAnimationActive={false} />
            <Line type="monotone" dataKey={totalKey} stroke={COLOR_BLUE} strokeWidth={2} name="Tổng" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey={failKey} stroke={COLOR_RED} strokeWidth={2} name="Fail" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// accounts_purchased (trục trái) và money_spent_vnd (trục phải) - 2 trục
// riêng (không normalize) để 1 line vài chục account và 1 line vài triệu
// VNĐ đều hiển thị rõ nét dao động, không bị line tiền "nuốt chửng" line
// account - cùng kỹ thuật dual Y-axis như UsageChart.js (ApiStatusPage).
function PurchaseChart({ data }) {
  return (
    <div className="rounded-xl border border-cyan-100 bg-white p-1.5">
      <h4 className="mb-0.5 text-[11px] font-semibold leading-tight text-slate-600">Accounts mua / Tiền mua</h4>
      <div className="h-[90px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} isAnimationActive={false} margin={{ top: 2, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="minute" tick={{ fontSize: 9 }} minTickGap={24} />
            <YAxis
              yAxisId="y_accounts"
              tick={{ fontSize: 9 }}
              allowDecimals={false}
              width={28}
              stroke={COLOR_BLUE}
            />
            <YAxis
              yAxisId="y_money"
              orientation="right"
              tick={{ fontSize: 9 }}
              width={36}
              stroke={COLOR_GREEN}
            />
            <Tooltip isAnimationActive={false} />
            <Line
              yAxisId="y_accounts"
              type="monotone"
              dataKey="accounts_purchased"
              stroke={COLOR_BLUE}
              strokeWidth={2}
              name="Accounts"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              yAxisId="y_money"
              type="monotone"
              dataKey="money_spent_vnd"
              stroke={COLOR_GREEN}
              strokeWidth={2}
              name="VNĐ"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function MetricsCharts() {
  const [metrics, setMetrics] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const data = await getMetricsSeries();
      setMetrics(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchData]);

  if (!metrics) return null;

  const requestData = {
    fb_ql: zipByMinute(metrics, ['request_fb_ql']),
    fb_eaaaau: zipByMinute(metrics, ['request_fb_eaaaau']),
    fb_eaagno: zipByMinute(metrics, ['request_fb_eaagno']),
  };

  const recoveryData = {};
  for (const serviceType of ['fb_ql', 'fb_eaaaau', 'fb_eaagno']) {
    const successKey = `recovery_success_${serviceType}`;
    const failKey = `recovery_fail_${serviceType}`;
    const zipped = zipByMinute(metrics, [successKey, failKey]);
    recoveryData[serviceType] = zipped.map((row) => ({
      minute: row.minute,
      total: row[successKey] + row[failKey],
      fail: row[failKey],
    }));
  }

  const reloginZipped = zipByMinute(metrics, ['relogin_live', 'relogin_fail']);
  const reloginData = reloginZipped.map((row) => ({
    minute: row.minute,
    total: row.relogin_live + row.relogin_fail,
    fail: row.relogin_fail,
  }));

  const purchaseData = zipByMinute(metrics, ['accounts_purchased', 'money_spent_vnd']);

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-3">
        <RequestBarChart title={`Request ${SERVICE_TYPE_LABEL.fb_ql}`} data={requestData.fb_ql} dataKey="request_fb_ql" />
        <RequestBarChart title={`Request ${SERVICE_TYPE_LABEL.fb_eaaaau}`} data={requestData.fb_eaaaau} dataKey="request_fb_eaaaau" />
        <RequestBarChart title={`Request ${SERVICE_TYPE_LABEL.fb_eaagno}`} data={requestData.fb_eaagno} dataKey="request_fb_eaagno" />
      </div>

      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-3">
        <SuccessFailLineChart title={`Recovery ${SERVICE_TYPE_LABEL.fb_ql}`} data={recoveryData.fb_ql} totalKey="total" failKey="fail" />
        <SuccessFailLineChart title={`Recovery ${SERVICE_TYPE_LABEL.fb_eaaaau}`} data={recoveryData.fb_eaaaau} totalKey="total" failKey="fail" />
        <SuccessFailLineChart title={`Recovery ${SERVICE_TYPE_LABEL.fb_eaagno}`} data={recoveryData.fb_eaagno} totalKey="total" failKey="fail" />
      </div>

      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        <SuccessFailLineChart title="Relogin" data={reloginData} totalKey="total" failKey="fail" />
        <PurchaseChart data={purchaseData} />
      </div>
    </div>
  );
}
